/**
 * Administrar Intereses — migración web de ares3030 (escritorio).
 *
 * Aplica descuentos de interés cuota por cuota a un préstamo activo: el
 * operador teclea el descuento por cuota (hasta el interés disponible de cada
 * una) y el backend valida la regla secuencial — para descontar la cuota N+1
 * la cuota N debe quedar completamente descontada (/api/descuentointeres_aplicar
 * devuelve data.Error si no). "Eliminar todos" revierte todos los descuentos
 * del préstamo (/api/descuentointeres_eliminar).
 */
import React, { useState, useEffect, useMemo } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import InputAdornment from "@mui/material/InputAdornment";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import RequestQuoteOutlinedIcon from "@mui/icons-material/RequestQuoteOutlined";
import PercentOutlinedIcon from "@mui/icons-material/PercentOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { useGlobalMDAlert } from "context";
import { FP, PageHeader, fpInputSx } from "components/financepro";
import { useApi } from "lib/api";

const filtrarPrestamos = createFilterOptions({
  stringify: (o) =>
    typeof o === "string"
      ? o
      : `${o.noprest} ${o.nombres || ""} ${o.cedula || ""} ${o.chasis || ""}`,
  limit: 60,
});

const fmtMoney = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
  if (Number.isNaN(n)) return "0.00";
  return n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const toNum = (v) => {
  const n = parseFloat(String(v ?? "").replace(/,/g, ""));
  return Number.isNaN(n) ? 0 : n;
};

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});
const btnGhost = {
  ...btnBase,
  color: FP.color.textSecondary,
  bgcolor: "transparent",
  "&:hover": { bgcolor: FP.color.surfaceAlt, color: FP.color.textPrimary },
};

// Dentro del hub de Configuración el marco lo pone el panel derecho: la
// sección va sin tarjeta propia, solo con separación entre bloques.
const sectionBoxHub = { mb: 3 };

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 2.4,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

export default function DescuentosInteres({ noprestInicial, enHub } = {}) {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();

  const [prestamos, setPrestamos] = useState([]);
  const [cargandoLista, setCargandoLista] = useState(true);
  const [prestamo, setPrestamo] = useState(null); // opción del autocomplete
  const [cuotas, setCuotas] = useState([]);
  const [descontado, setDescontado] = useState(0); // total ya descontado (backend)
  const [descuentos, setDescuentos] = useState({}); // nocuota -> string tecleado
  const [cargando, setCargando] = useState(false);

  const [aplicando, setAplicando] = useState(false);
  const [eliminarOpen, setEliminarOpen] = useState(false);
  const [eliminando, setEliminando] = useState(false);

  const cargarPrestamos = async () => {
    setCargandoLista(true);
    try {
      const { ok, data } = await post("/api/refinanciamiento_prestamos", {});
      if (ok) setPrestamos(data.prestamos || []);
      else
        showAlert({ message: data.Error || "No se pudieron cargar los préstamos", color: "error" });
    } finally {
      setCargandoLista(false);
    }
  };

  useEffect(() => {
    cargarPrestamos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cargarDatos = async (np) => {
    setCargando(true);
    try {
      const { ok, data } = await post("/api/descuentointeres_datos", { noprest: np });
      if (!ok) {
        showAlert({ message: data.Error || "No se pudo recuperar el préstamo", color: "error" });
        setPrestamo(null);
        return;
      }
      setCuotas(data.cuotas || []);
      setDescontado(toNum(data.descontado));
      setDescuentos({});
    } finally {
      setCargando(false);
    }
  };

  // Preselección: al abrir desde el expediente ya viene el préstamo elegido.
  const [preselListo, setPreselListo] = useState(false);
  useEffect(() => {
    if (!noprestInicial || preselListo || !prestamos.length) return;
    const opcion = prestamos.find((x) => String(x.noprest) === String(noprestInicial));
    if (opcion) elegirPrestamo(opcion);
    setPreselListo(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prestamos]);

  const elegirPrestamo = async (p) => {
    setPrestamo(p);
    setCuotas([]);
    setDescontado(0);
    setDescuentos({});
    if (!p) return;
    await cargarDatos(p.noprest);
  };

  const limpiar = () => {
    setPrestamo(null);
    setCuotas([]);
    setDescontado(0);
    setDescuentos({});
    setEliminarOpen(false);
  };

  const cambiarDescuento = (c, texto) => {
    // Tope local: nunca más que el interés disponible de la cuota. La regla
    // secuencial (cuota N completa antes de la N+1) la valida el backend.
    const max = toNum(c.disponible);
    const v = Math.max(0, Math.min(toNum(texto), max));
    setDescuentos((prev) => ({ ...prev, [c.nocuota]: texto === "" ? "" : String(v) }));
  };

  const totalNuevo = useMemo(
    () => Object.values(descuentos).reduce((s, v) => s + toNum(v), 0),
    [descuentos]
  );

  const aplicar = async () => {
    if (!prestamo) {
      showAlert({ message: "Debe seleccionar un préstamo", color: "warning" });
      return;
    }
    const detalle = cuotas
      .map((c) => ({ nocuota: c.nocuota, valor: toNum(descuentos[c.nocuota]) }))
      .filter((d) => d.valor > 0);
    if (!detalle.length) {
      showAlert({ message: "Indique al menos un descuento", color: "warning" });
      return;
    }
    setAplicando(true);
    try {
      const { ok, data } = await post("/api/descuentointeres_aplicar", {
        noprest: prestamo.noprest,
        detalle,
      });
      if (ok) {
        showAlert({ message: data.ok || "Descuentos aplicados", color: "success" });
        // Saldado por el descuento: ya no tiene cuotas pendientes que recargar
        // y sale de la lista de activos.
        if (data.saldado) {
          limpiar();
          cargarPrestamos();
        } else {
          await cargarDatos(prestamo.noprest);
        }
      } else {
        showAlert({
          message: data.Error || "No se pudieron aplicar los descuentos",
          color: "error",
        });
      }
    } finally {
      setAplicando(false);
    }
  };

  const eliminarTodos = async () => {
    setEliminando(true);
    try {
      const { ok, data } = await post("/api/descuentointeres_eliminar", {
        noprest: prestamo.noprest,
      });
      if (ok) {
        showAlert({ message: data.ok || "Descuentos eliminados", color: "success" });
        setEliminarOpen(false);
        await cargarDatos(prestamo.noprest);
      } else {
        showAlert({
          message: data.Error || "No se pudieron eliminar los descuentos",
          color: "error",
        });
        setEliminarOpen(false);
      }
    } finally {
      setEliminando(false);
    }
  };

  const chipInfo = {
    bgcolor: FP.color.surfaceAlt,
    color: FP.color.textSecondary,
    fontWeight: 700,
  };

  const thSx = {
    fontWeight: 800,
    fontSize: "0.7rem",
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: FP.color.textMuted,
    borderBottom: `1px solid ${FP.color.border}`,
    p: 1.2,
    textAlign: "left",
  };
  const tdSx = {
    fontSize: "0.85rem",
    color: FP.color.textPrimary,
    borderBottom: `1px solid ${FP.color.border}`,
    p: 1.2,
    fontVariantNumeric: "tabular-nums",
  };

  return (
    <Box
      component="main"
      sx={enHub ? undefined : { minHeight: "100vh", bgcolor: FP.color.surface }}
    >
      <PageHeader
        compact={enHub}
        title="Administrar Intereses"
        subtitle="Aplica o elimina descuentos de interés por cuota de un préstamo (ares3030)"
        cta={
          <Button startIcon={<RestartAltIcon />} onClick={limpiar} sx={btnOutline(FP.color.navy)}>
            Nuevo
          </Button>
        }
      />

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {/* Préstamo */}
        <Box sx={enHub ? sectionBoxHub : sectionBox}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <Autocomplete
                autoHighlight
                selectOnFocus
                handleHomeEndKeys
                filterOptions={filtrarPrestamos}
                options={prestamos}
                loading={cargandoLista || cargando}
                value={prestamo}
                getOptionLabel={(o) =>
                  typeof o === "string" ? o : `${o.noprest} — ${o.nombres || ""}`
                }
                isOptionEqualToValue={(o, v) => (o.noprest || o) === (v.noprest || v)}
                onChange={(_, v) => elegirPrestamo(v)}
                renderOption={(props, o) => (
                  <li {...props} key={o.noprest}>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
                        {o.noprest} — {o.nombres}
                      </Typography>
                      <Typography sx={{ fontSize: "0.72rem", color: FP.color.textMuted }}>
                        {[o.cedula, o.chasis, o.sucursal].filter(Boolean).join(" · ")}
                      </Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Préstamo"
                    helperText="Busca por número, nombre, cédula o chasis"
                    sx={fpInputSx}
                  />
                )}
              />
            </Grid>
            {prestamo && (
              <Grid item xs={12} md={6}>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip label={`#${prestamo.noprest} — ${prestamo.nombres || ""}`} sx={chipInfo} />
                  <Chip
                    icon={<PercentOutlinedIcon />}
                    label={`Total descontado: RD$ ${fmtMoney(descontado)}`}
                    sx={{
                      bgcolor: FP.color.accentBlueSoft,
                      color: FP.color.accentBlue,
                      fontWeight: 700,
                    }}
                  />
                  {totalNuevo > 0 && (
                    <Chip
                      label={`Por aplicar: RD$ ${fmtMoney(totalNuevo)}`}
                      sx={{
                        bgcolor: `${FP.color.success}14`,
                        color: FP.color.success,
                        fontWeight: 700,
                      }}
                    />
                  )}
                </Stack>
              </Grid>
            )}
          </Grid>
        </Box>

        {/* Cuotas */}
        <Card
          elevation={0}
          sx={{
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            boxShadow: FP.shadow.soft,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: 2.5,
              py: 1.8,
              borderBottom: `1px solid ${FP.color.border}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <RequestQuoteOutlinedIcon sx={{ fontSize: 18, color: FP.color.textSecondary }} />
              <Typography sx={{ fontWeight: 800, color: FP.color.textPrimary }}>
                Cuotas del préstamo
              </Typography>
              {cargando && <CircularProgress size={16} />}
            </Stack>
            <Stack direction="row" spacing={1.2}>
              <Button
                startIcon={
                  aplicando ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <CheckCircleOutlineIcon />
                  )
                }
                onClick={aplicar}
                disabled={aplicando || !prestamo || totalNuevo <= 0}
                sx={btnSolid(FP.color.navy, FP.color.navySoft)}
              >
                Aplicar descuentos
              </Button>
              <Button
                startIcon={<DeleteOutlineIcon />}
                onClick={() => setEliminarOpen(true)}
                disabled={!prestamo || descontado <= 0}
                sx={btnOutline(FP.color.error)}
              >
                Eliminar todos
              </Button>
            </Stack>
          </Box>
          <Box sx={{ overflowX: "auto", bgcolor: "#fff" }}>
            <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
              <Box component="thead">
                <Box component="tr">
                  <Box component="th" sx={{ ...thSx, textAlign: "center" }}>
                    Cuota
                  </Box>
                  <Box component="th" sx={thSx}>
                    Fecha
                  </Box>
                  <Box component="th" sx={{ ...thSx, textAlign: "right" }}>
                    Valor cuota
                  </Box>
                  <Box component="th" sx={{ ...thSx, textAlign: "right" }}>
                    Ya descontado
                  </Box>
                  <Box component="th" sx={{ ...thSx, textAlign: "right" }}>
                    Disponible
                  </Box>
                  <Box component="th" sx={{ ...thSx, width: 180 }}>
                    Descuento
                  </Box>
                </Box>
              </Box>
              <Box component="tbody">
                {cuotas.map((c) => (
                  <Box component="tr" key={c.nocuota}>
                    <Box component="td" sx={{ ...tdSx, textAlign: "center", fontWeight: 700 }}>
                      {c.nocuota}
                    </Box>
                    <Box component="td" sx={tdSx}>
                      {c.fecha}
                    </Box>
                    <Box component="td" sx={{ ...tdSx, textAlign: "right" }}>
                      RD$ {fmtMoney(c.cuota)}
                    </Box>
                    <Box
                      component="td"
                      sx={{
                        ...tdSx,
                        textAlign: "right",
                        color: toNum(c.descinte) > 0 ? FP.color.accentBlue : FP.color.textMuted,
                        fontWeight: toNum(c.descinte) > 0 ? 700 : 400,
                      }}
                    >
                      RD$ {fmtMoney(c.descinte)}
                    </Box>
                    <Box component="td" sx={{ ...tdSx, textAlign: "right" }}>
                      RD$ {fmtMoney(c.disponible)}
                    </Box>
                    <Box component="td" sx={tdSx}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="Descuento"
                        value={descuentos[c.nocuota] ?? ""}
                        onChange={(e) => cambiarDescuento(c, e.target.value)}
                        disabled={toNum(c.disponible) <= 0}
                        sx={fpInputSx}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">RD$</InputAdornment>,
                        }}
                      />
                    </Box>
                  </Box>
                ))}
                {!cuotas.length && (
                  <Box component="tr">
                    <Box
                      component="td"
                      colSpan={6}
                      sx={{ ...tdSx, textAlign: "center", color: FP.color.textMuted, py: 4 }}
                    >
                      {prestamo
                        ? "El préstamo no tiene cuotas pendientes"
                        : "Seleccione un préstamo para ver sus cuotas"}
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
          {!!cuotas.length && (
            <Box sx={{ px: 2.5, py: 1.6, borderTop: `1px solid ${FP.color.border}` }}>
              <Typography sx={{ fontSize: "0.78rem", color: FP.color.textMuted }}>
                Para descontar una cuota, la anterior debe quedar completamente descontada — el
                backend valida la secuencia y rechaza descuentos fuera de orden.
              </Typography>
            </Box>
          )}
        </Card>
      </Box>

      {/* Confirmación: eliminar todos los descuentos */}
      <Dialog
        open={eliminarOpen}
        onClose={() => !eliminando && setEliminarOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, display: "flex", alignItems: "center", gap: 1.2 }}>
          <WarningAmberOutlinedIcon sx={{ color: FP.color.error }} />
          Eliminar todos los descuentos
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "0.9rem", color: FP.color.textSecondary }}>
            Se eliminarán <b>TODOS</b> los descuentos de interés del préstamo{" "}
            <b>#{prestamo?.noprest}</b> (RD$ {fmtMoney(descontado)} descontados) y las cuotas
            volverán a su interés original. ¿Desea continuar?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setEliminarOpen(false)} disabled={eliminando} sx={btnGhost}>
            No
          </Button>
          <Button
            startIcon={
              eliminando ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineIcon />
            }
            onClick={eliminarTodos}
            disabled={eliminando}
            sx={btnSolid(FP.color.error, "#B71C1C")}
          >
            Sí, eliminar todos
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
